
// List of major automotive brands to act as Parent Categories
export const MAJOR_BRANDS = [
    'bmw',
    'toyota',
    'mercedes-benz',
    'audi',
    'volkswagen',
    'ford',
    'hyundai',
    'honda',
    'nissan',
    'porsche',
    'ferrari',
    'lamborghini',
    'tesla',
    'suzuki',
    'kia',
    'renault',
    'chery',
    'haval',
    'isuzu',
    'jeep',
    'land-rover',
    'lexus',
    'mazda',
    'mini',
    'mitsubishi',
    'peugeot',
    'subaru',
    'volvo',
    'dacia',
    'changan',
    'jmc',
    'aston-martin',
    'gwm',
    'ineos',
    'mahindra',
    'tata',
    'fiat',
    'alfa-romeo',
    'maserati',
    'mclaren',
    'bentley',
    'rolls-royce',
    'jetour',
    'omoda',
    'jaecoo',
    'stellantis',
    'mg',
    'byd',
    'proton',
    'ssangyong',
    'ldv'
];

export interface Category {
    name: string;
    slug: string;
    count?: number; // Optional count of articles
}

export interface HierarchicalCategory extends Category {
    children: Category[];
}

/**
 * Organizes a flat list of categories into a Brand-based hierarchy.
 * @param categories Flat list of categories
 */
export function organizeCategories(categories: Category[]): HierarchicalCategory[] {
    const hierarchy: HierarchicalCategory[] = [];
    const usedSlugs = new Set<string>();

    // 1. Identify Parents (Brands)
    // We look for categories that exactly match our MAJOR_BRANDS list
    for (const brandSlug of MAJOR_BRANDS) {
        const match = categories.find(c => c?.slug === brandSlug);
        if (match) {
            hierarchy.push({
                ...match,
                children: []
            });
            usedSlugs.add(match.slug);
        }
    }

    // 2. Assign Children (Sub-categories)
    // Iterate through all categories again.
    for (const cat of categories) {
        if (usedSlugs.has(cat.slug)) continue; // Skip if already a parent

        let assigned = false;

        // Check if this category belongs to any parent
        for (const parent of hierarchy) {
            // Logic: Child slug starts with parent slug (e.g. 'bmw-m3' starts with 'bmw')
            // AND strictly has a dash separator right after match to avoid 'bmwxyz' matching 'bmw'
            // OR if the user named it 'bmwawards' (unlikely but possible), usually 'bmw-awards'

            // We strip hyphens to compare strictly "contains" or standard prefix logic
            if (cat?.slug?.startsWith(`${parent.slug}-`)) {
                parent.children.push(cat);
                usedSlugs.add(cat.slug);
                assigned = true;
                break; // Stop looking for parents, it's assigned
            }
        }

        // If not assigned to any brand/parent, it remains a top-level category (e.g. "News", "Reviews")
        // We can optionally add these to the hierarchy root if desired.
        // For now, let's keep the return strictly the mapped Brands + their children 
        // OR we return everything but properly nested.
    }

    // Sort children alphabetically
    hierarchy.forEach(parent => {
        parent.children.sort((a, b) => a.name.localeCompare(b.name));
    });

    return hierarchy;
}

/**
 * Helper to Find Parent for a given Child Slug
 * Useful for redirects
 */
export function findParentCategory(childSlug: string, categories: Category[]): string | null {
    // 1. Is this strictly one of our known parents? Then no parent.
    if (MAJOR_BRANDS.includes(childSlug)) return null;

    // 2. Check major brands prefix
    for (const brand of MAJOR_BRANDS) {
        if (childSlug.startsWith(`${brand}-`)) {
            // Verify if the brand actually exists in our DB categories just to be safe?
            // For now, simple string matching is likely sufficient and faster.
            return brand;
        }
    }
    return null;
}
